import React, { useState, useContext, useEffect } from 'react';
import style from './PlanBuilderPage.module.css';
import axios from 'axios';
import { toast } from 'react-toastify';
import InterestChoiceCard from '../../components/InterestChoiceCard/InterestChoiceCard';
import { UserContext } from '../../contexts/UserContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuthUser } from '../../hooks/useAuthUser';
import { Link, useNavigate } from 'react-router-dom';
import Checkbox from '../../components/CheckBox/CheckBox';
import RadioButton from '../../components/RadioButton/RadioButton';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton';
import FormInput from '../../components/FormInput/FormInput';
import { checkSubscription } from '../../utils/checkSubscription';
import {
	MdCalendarMonth,
	MdPerson,
	MdFitnessCenter,
	MdFlag,
	MdArrowBack,
	MdCheck,
} from 'react-icons/md';

const interestsArray = [
	'Running', 'Home Workouts', 'Swimming', 'Lifting', 'Yoga', 'Cardio',
	'Boxing', 'Pilates', 'HIIT', 'CrossFit', 'Dance', 'Martial Arts',
	'Functional Training', 'Cycling', 'TRX Training', 'Calisthenics',
	'Pelvic Floor',
];

const equipmentArray = [
	'Dumbbells', 'Treadmill', 'Resistance Bands', 'Barbell', 'Kettlebell',
	'Pull-Up Bar', 'Yoga Mat', 'Rowing Machine', 'Exercise Bike',
	'Medicine Ball', 'Bench Press', 'Jump Ropes', 'Stability Ball',
	'Battle Ropes', 'Cable Machine',
];

const ageRangeArray = ['13 - 17', '18 - 30', '31 - 50', '51+'];
const genderArray = ['Male', 'Female', 'Other'];
const fitnessLevelArray = ['Beginner', 'Intermediate', 'Advanced'];
const styleArray = ['Short and Intense', 'Long and moderate'];
const durationArray = ['30 mins', '45 mins', '1 hr'];

const planDurations = [
	{
		days: 7,
		title: '7-Day Plan',
		subtitle: 'Quick Start',
		description: 'Perfect for building a weekly habit. Ideal for beginners or those wanting a structured week.',
	},
	{
		days: 14,
		title: '14-Day Plan',
		subtitle: 'Foundation',
		description: 'Two full weeks of progressive training. See real results with consistency and variety.',
	},
	{
		days: 30,
		title: '30-Day Plan',
		subtitle: 'Transformation',
		description: 'A full month of structured progression. Build lasting habits and achieve meaningful change.',
	},
];

const API_URL = process.env.REACT_APP_API_URL;

const PlanBuilderPage = () => {
	const { user } = useAuthUser();
	const navigate = useNavigate();
	useTheme();

	const [currentStep, setCurrentStep] = useState(1);
	const { dbUser } = useContext(UserContext);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [planConfig, setPlanConfig] = useState({
		planDuration: null,
		type: 'bodyweight',
		workoutInterests: [],
		workoutEquipments: [],
		ageRange: '',
		gender: '',
		level: '',
		style: '',
		duration: '',
		goal: '',
		additionalText: '',
	});

	useEffect(() => {
		if (!user) {
			navigate(`/login?redirectTo=${encodeURIComponent('/plan-builder')}`, { replace: true });
		}
	}, [user, navigate]);

	const steps = [
		{ id: 1, title: 'Duration', icon: MdCalendarMonth },
		{ id: 2, title: 'About You', icon: MdPerson },
		{ id: 3, title: 'Workout Style', icon: MdFitnessCenter },
		{ id: 4, title: 'Goals', icon: MdFlag },
	];

	const submitPlan = async () => {
		const { level, workoutInterests, style, duration, goal, planDuration } = planConfig;
		if (!planDuration) { toast('Please select a plan duration.'); return; }
		if (!level) { toast('Please select your fitness level.'); return; }
		if (!workoutInterests.length) { toast('Please choose at least one workout interest.'); return; }
		if (!style) { toast('Please choose a workout style.'); return; }
		if (!duration) { toast('Please choose a session duration.'); return; }
		if (!goal) { toast('Please enter your workout goal.'); return; }

		setIsSubmitting(true);

		try {
			await axios.post(`${API_URL}/interests/${user.id}`, planConfig);
			navigate('/generated-plan', { state: { planConfig } });
		} catch (err) {
			console.error(err);
			toast('Something went wrong. Please try again.');
			setIsSubmitting(false);
		}
	};

	const nextStep = () => {
		const { planDuration, level, workoutInterests, workoutEquipments, type, style, duration } = planConfig;
		if (currentStep === 1 && !planDuration) {
			toast('Please select a plan duration.'); return;
		}
		if (currentStep === 2 && !level) {
			toast('Please select a fitness level.'); return;
		}
		if (currentStep === 3) {
			if (!workoutInterests.length) {
				toast('Please select at least one workout interest.'); return;
			}
			if (type === 'equipments' && !workoutEquipments.length) {
				toast('Please select at least one equipment.'); return;
			}
		}
		if (currentStep === 4) {
			if (!style || !duration) {
				toast('Please select both a workout style and duration.'); return;
			}
		}
		if (currentStep < 4) setCurrentStep(currentStep + 1);
	};

	const prevStep = () => {
		if (currentStep > 1) setCurrentStep(currentStep - 1);
	};

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, [currentStep]);

	const update = (field) => (val) => {
		setPlanConfig(prev => ({ ...prev, [field]: typeof val === 'function' ? val(prev[field]) : val }));
	};

	const toggleArrayItem = (field) => (item) => (isChecked) => {
		setPlanConfig(prev => ({
			...prev,
			[field]: isChecked
				? [...prev[field], item]
				: prev[field].filter(i => i !== item),
		}));
	};

	if (user && !checkSubscription(dbUser)) {
		return (
			<div className={style.PlanBuilderPage}>
				<div className={style.UpgradeWrapper}>
					<div className={style.UpgradeContent}>
						<MdCalendarMonth className={style.UpgradeIcon} />
						<h2>Unlock Workout Plans</h2>
						<p>Upgrade to Calcium Plus to create AI-powered multi-day workout plans tailored to your goals.</p>
						<Link to="/upgrade">
							<PrimaryButton text="Upgrade Now" click={() => null} />
						</Link>
					</div>
				</div>
			</div>
		);
	}

	const renderDurationStep = () => (
		<div className={style.StepContent}>
			<div className={style.StepSection}>
				<h3 className={style.StepTitle}>How long is your plan?</h3>
				<p className={style.StepDescription}>Choose a duration that matches your commitment level. You can always create another plan later.</p>
				<div className={style.DurationGrid}>
					{planDurations.map((plan) => (
						<button
							key={plan.days}
							className={`${style.DurationCard} ${planConfig.planDuration === plan.days ? style.DurationSelected : ''}`}
							onClick={() => update('planDuration')(plan.days)}
						>
							<div className={style.DurationBadge}>
								{planConfig.planDuration === plan.days && <MdCheck />}
							</div>
							<span className={style.DurationDays}>{plan.days}</span>
							<span className={style.DurationUnit}>days</span>
							<span className={style.DurationTitle}>{plan.subtitle}</span>
							<p className={style.DurationDesc}>{plan.description}</p>
						</button>
					))}
				</div>
			</div>
		</div>
	);

	const renderAboutStep = () => (
		<div className={style.StepContent}>
			<div className={style.StepSection}>
				<h3 className={style.StepTitle}>Tell us about yourself</h3>
				<p className={style.StepDescription}>This helps us build a plan that fits your body and experience.</p>
				<div className={style.FormSection}>
					<div className={style.FormGroup}>
						<label className={style.FormLabel}>Age Range</label>
						<div className={style.RadioGroup}>
							{ageRangeArray.map(age => (
								<RadioButton key={age} label={age} value={age} name="age"
									isChecked={planConfig.ageRange === age}
									onRadioChange={() => update('ageRange')(age)} />
							))}
						</div>
					</div>
					<div className={style.FormGroup}>
						<label className={style.FormLabel}>Gender</label>
						<div className={style.RadioGroup}>
							{genderArray.map(g => (
								<RadioButton key={g} label={g} value={g} name="gender"
									isChecked={planConfig.gender === g}
									onRadioChange={() => update('gender')(g)} />
							))}
						</div>
					</div>
					<div className={style.FormGroup}>
						<label className={style.FormLabel}>Fitness Level <span className={style.Required}>*</span></label>
						<div className={style.RadioGroup}>
							{fitnessLevelArray.map(lvl => (
								<RadioButton key={lvl} label={lvl} value={lvl} name="levels"
									isChecked={planConfig.level === lvl}
									onRadioChange={() => update('level')(lvl)} />
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	const renderWorkoutStyleStep = () => (
		<div className={style.StepContent}>
			<div className={style.StepSection}>
				<h3 className={style.StepTitle}>Choose your workout style</h3>
				<p className={style.StepDescription}>Select the type of workouts you enjoy and have access to.</p>
				<div className={style.FormSection}>
					<div className={style.FormGroup}>
						<label className={style.FormLabel}>Workout Type</label>
						<div className={style.ChoiceCardsWrapper}>
							<InterestChoiceCard
								Title="Bodyweight"
								Text="No equipment needed."
								click={() => update('type')('bodyweight')}
								selected={planConfig.type === 'bodyweight'}
							/>
							<InterestChoiceCard
								Title="With Equipment"
								Text="Use gym or home equipment."
								click={() => update('type')('equipments')}
								selected={planConfig.type === 'equipments'}
							/>
						</div>
					</div>
					<div className={style.FormGroup}>
						<label className={style.FormLabel}>Workout Interests <span className={style.Required}>*</span></label>
						<div className={style.InterestsGrid}>
							{interestsArray.map(interest => (
								<Checkbox key={interest} label={interest}
									isChecked={planConfig.workoutInterests.includes(interest)}
									onCheckboxChange={toggleArrayItem('workoutInterests')(interest)} />
							))}
						</div>
					</div>
					{planConfig.type === 'equipments' && (
						<div className={style.FormGroup}>
							<label className={style.FormLabel}>Available Equipment</label>
							<div className={style.InterestsGrid}>
								{equipmentArray.map(eq => (
									<Checkbox key={eq} label={eq}
										isChecked={planConfig.workoutEquipments.includes(eq)}
										onCheckboxChange={toggleArrayItem('workoutEquipments')(eq)} />
								))}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);

	const renderGoalsStep = () => (
		<div className={style.StepContent}>
			<div className={style.StepSection}>
				<h3 className={style.StepTitle}>Preferences & Goals</h3>
				<p className={style.StepDescription}>Fine-tune your plan to match your schedule and ambitions.</p>
				<div className={style.FormSection}>
					<div className={style.FormGroup}>
						<label className={style.FormLabel}>Workout Style <span className={style.Required}>*</span></label>
						<div className={style.RadioGroup}>
							{styleArray.map(s => (
								<RadioButton key={s} label={s} value={s} name="styles"
									isChecked={planConfig.style === s}
									onRadioChange={() => update('style')(s)} />
							))}
						</div>
					</div>
					<div className={style.FormGroup}>
						<label className={style.FormLabel}>Session Duration <span className={style.Required}>*</span></label>
						<div className={style.RadioGroup}>
							{durationArray.map(d => (
								<RadioButton key={d} label={d} value={d} name="durations"
									isChecked={planConfig.duration === d}
									onRadioChange={() => update('duration')(d)} />
							))}
						</div>
					</div>
					<div className={style.FormGroup}>
						<label className={style.FormLabel}>What are your goals? <span className={style.Required}>*</span></label>
						<FormInput HeadingText="" placeholder="e.g. Build muscle, lose weight, improve endurance..."
							change={(e) => update('goal')(e.target.value)} width="100%" showIsEmpty={true} />
					</div>
					<div className={style.FormGroup}>
						<label className={style.FormLabel}>Additional Info</label>
						<FormInput HeadingText="" placeholder="Any injuries, limitations, or preferences..."
							change={(e) => update('additionalText')(e.target.value)} width="100%" showIsEmpty={true} />
					</div>
				</div>
			</div>
		</div>
	);

	const renderStepContent = () => {
		switch (currentStep) {
			case 1: return renderDurationStep();
			case 2: return renderAboutStep();
			case 3: return renderWorkoutStyleStep();
			case 4: return renderGoalsStep();
			default: return null;
		}
	};

	return (
		<div className={style.PlanBuilderPage}>
			<div className={style.Header}>
				<button className={style.BackButton} onClick={() => navigate(-1)}>
					<MdArrowBack />
					<span>Back</span>
				</button>
				<div className={style.HeaderContent}>
					<div className={style.PlanBadge}>
						<MdCalendarMonth />
						<span>{planConfig.planDuration ? `${planConfig.planDuration}-Day Plan` : 'Workout Plan'}</span>
					</div>
					<h1 className={style.PageTitle}>Build Your Workout Plan</h1>
					<p className={style.PageDescription}>
						Create a structured, multi-day program that evolves with you.
					</p>
				</div>
			</div>

			<div className={style.ProgressContainer}>
				<div className={style.ProgressBar}>
					<div className={style.ProgressFill} style={{ width: `${(currentStep / steps.length) * 100}%` }} />
				</div>
				<div className={style.StepsIndicator}>
					{steps.map((step) => (
						<div key={step.id}
							className={`${style.StepIndicator} ${currentStep >= step.id ? style.Active : ''} ${currentStep === step.id ? style.Current : ''}`}>
							<div className={style.StepIcon}>
								{currentStep > step.id ? <MdCheck /> : <step.icon />}
							</div>
							<span className={style.StepText}>{step.title}</span>
						</div>
					))}
				</div>
			</div>

			{renderStepContent()}

			<div className={style.NavigationButtons}>
				{currentStep > 1 && (
					<SecondaryButton text="Back" click={prevStep} />
				)}
				<div className={style.NavSpacer} />
				{currentStep < 4 ? (
					<PrimaryButton text="Continue" click={nextStep} />
				) : (
					<PrimaryButton
						text={isSubmitting ? 'Building Plan...' : 'Generate Plan'}
						click={submitPlan}
						loading={isSubmitting}
					/>
				)}
			</div>
		</div>
	);
};

export default PlanBuilderPage;
